import { useState, useEffect, useMemo, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Upload, Wand2, Loader2, RefreshCw } from "lucide-react";
import { api, apiError, graphProductToCard, imgUrl } from "@/lib/api";
import { launchCashfree } from "@/lib/cashfreeCheckout";
import PayMethodPicker from "@/components/PayMethodPicker";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLocale } from "@/context/LocaleContext";
import { APPAREL_CATEGORIES, productFamily } from "@/constants/shop";

export default function TryOn() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { country, currency, formatMoney, countries } = useLocale();
  const [params] = useSearchParams();
  const fileRef = useRef(null);
  const [personPath, setPersonPath] = useState(null);
  const [personPreview, setPersonPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState(params.get("product") || "");
  const [garmentDraft, setGarmentDraft] = useState("");
  const [garmentQ, setGarmentQ] = useState("");
  const [garmentCat, setGarmentCat] = useState("All");
  const [brandId, setBrandId] = useState("");
  const [result, setResult] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [buying, setBuying] = useState("");
  const [showPaywall, setShowPaywall] = useState(false);
  const [buyPackId, setBuyPackId] = useState("starter");
  const [payPhone, setPayPhone] = useState("");
  const [payProvider, setPayProvider] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [more, setMore] = useState([]);
  const [pageToken, setPageToken] = useState(null);
  const [pastGarment, setPastGarment] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const pinnedId = params.get("product") || "";

  const garmentFilters = () => {
    const filters = { country, currency };
    if (garmentCat !== "All") filters.category = garmentCat;
    if (brandId) filters.brand_id = brandId;
    return filters;
  };
  const garmentQuery = () => garmentQ.trim() || (garmentCat === "All" && !brandId ? "clothing" : "");
  const keepGarment = (p) => {
    if (!p?.id) return false;
    const fam = productFamily(p);
    if (!garmentQ.trim() && fam && fam !== "apparel") return false;
    if (brandId && p.brand_id && p.brand_id !== brandId) return false;
    return true;
  };

  const { data: wallet } = useQuery({
    queryKey: ["tryon-credits"],
    queryFn: async () => (await api.get("/ai/tryon/credits")).data,
  });
  const { data: history = [] } = useQuery({
    queryKey: ["tryon-history"],
    queryFn: async () => (await api.get("/ai/tryon/history")).data?.tries || [],
  });
  const { data: brandData } = useQuery({
    queryKey: ["tryon-brands", garmentCat],
    queryFn: async () => (await api.get("/graph/external/brands", {
      params: garmentCat !== "All" ? { category: garmentCat } : {},
    })).data,
    staleTime: 10 * 60 * 1000,
  });
  const brands = brandData?.brands || [];

  const { data: garmentPage, isLoading: loadingGarments } = useQuery({
    queryKey: ["tryon-garments", garmentQ, garmentCat, brandId, country, currency, pinnedId],
    queryFn: async () => {
      const [native, graphRes, pinned] = await Promise.all([
        api.get("/products?limit=200").then(({ data }) => data || []).catch(() => []),
        api.post("/graph/search", {
          query: garmentQuery(),
          filters: garmentFilters(),
          limit: 30,
        }).then(({ data }) => data).catch(() => ({ products: [], next_page_token: null })),
        pinnedId
          ? api.get(`/products/${encodeURIComponent(pinnedId)}`).then(({ data }) => data).catch(() => null)
          : Promise.resolve(null),
      ]);
      const graphHits = (graphRes?.products || []).map(graphProductToCard);
      const merged = [];
      const seen = new Set();
      for (const p of [pinned, ...native, ...graphHits].filter(Boolean)) {
        if (!keepGarment(p) || seen.has(p.id)) continue;
        seen.add(p.id);
        merged.push(p);
      }
      return { products: merged, next_page_token: graphRes?.next_page_token || null };
    },
  });

  useEffect(() => {
    setMore([]);
    setPageToken(garmentPage?.next_page_token || null);
  }, [garmentQ, garmentCat, brandId, country, currency, garmentPage?.next_page_token]);

  const products = useMemo(() => {
    const merged = [];
    const seen = new Set();
    for (const p of [...(garmentPage?.products || []), ...more]) {
      if (!p?.id || seen.has(p.id)) continue;
      seen.add(p.id);
      merged.push(p);
    }
    return merged;
  }, [garmentPage, more]);

  useEffect(() => {
    if (!products.length) return;
    if (selected && (products.some((p) => p.id === selected) || pastGarment?.id === selected)) return;
    if (pinnedId && products.some((p) => p.id === pinnedId)) setSelected(pinnedId);
  }, [products, selected, pinnedId, pastGarment]);

  const loadMore = async () => {
    if (!pageToken || loadingMore) return;
    setLoadingMore(true);
    try {
      const { data: res } = await api.post("/graph/search", {
        query: garmentQuery(),
        filters: garmentFilters(),
        limit: 30,
        page_token: pageToken,
      });
      const extra = (res?.products || []).map(graphProductToCard).filter(keepGarment);
      setMore((prev) => [...prev, ...extra]);
      setPageToken(res?.next_page_token || null);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoadingMore(false);
    }
  };

  const selectedProduct = products.find((p) => p.id === selected)
    || (pastGarment && pastGarment.id === selected ? pastGarment : null);
  const remaining = wallet?.remaining ?? 3;
  const cashfreeOn = Boolean(wallet?.cashfree_configured);
  const stripeOn = Boolean(wallet?.stripe_configured);
  const india = (country || "").toUpperCase() === "IN";
  const countryName = (countries || []).find((c) => c.code === (country || "").toUpperCase())?.name || country;
  const payMethods = [
    {
      id: "cashfree",
      label: "Cashfree",
      audience: "India",
      summary: cashfreeOn
        ? "UPI, netbanking, RuPay, and Indian cards. Charged in rupees."
        : "Not turned on. Save the Cashfree App ID and secret in Admin → Settings.",
      ready: cashfreeOn,
      recommended: cashfreeOn && (india || !stripeOn),
    },
    {
      id: "stripe",
      label: "Stripe",
      audience: "Outside India",
      summary: stripeOn
        ? "Visa, Mastercard, Amex, and other international cards, in your currency."
        : "Not turned on. Save a Stripe secret key in Admin → Settings → Payments.",
      ready: stripeOn,
      recommended: stripeOn && (!india || !cashfreeOn),
    },
  ];
  const payNote = !stripeOn && cashfreeOn
    ? "Stripe is listed for cards outside India. It turns on after a Stripe secret key is saved in Admin → Settings → Payments."
    : !cashfreeOn && stripeOn
      ? "Cashfree is listed for India. It turns on after the Cashfree App ID and secret are saved in Admin → Settings."
      : india
        ? "You are paying from India. Use Cashfree for UPI, netbanking, and Indian cards, charged in rupees. Stripe is for an international card."
        : `You are paying from ${countryName || "outside India"}. Use Stripe for Visa, Mastercard, and other international cards in your currency. Cashfree is for shoppers in India who pay with UPI or an Indian card.`;

  useEffect(() => {
    const next = payMethods.find((m) => m.recommended)?.id;
    if (next) setPayProvider(next);
  }, [india, cashfreeOn, stripeOn]);

  useEffect(() => {
    const saved = user?.profile?.address_phone || "";
    if (saved && !payPhone) setPayPhone(saved);
  }, [user?.profile?.address_phone, payPhone]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPersonPreview(URL.createObjectURL(file));
    setResult(null);
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("folder", "persons");
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPersonPath(data.path);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploading(false);
    }
  };

  const generate = async () => {
    if (!personPath) return toast.error("Upload your photo first");
    if (!selected) return toast.error("Tap a garment below");
    if (wallet?.needs_credits) {
      setShowPaywall(true);
      return;
    }
    setGenerating(true);
    setResult(null);
    try {
      const { data } = await api.post("/ai/tryon", { product_id: selected, person_image_path: personPath }, { timeout: 180000 });
      setResult(data.image_url);
      qc.setQueryData(["tryon-credits"], data.wallet);
      qc.invalidateQueries({ queryKey: ["tryon-history"] });
      if (data.wallet?.needs_credits) setShowPaywall(true);
    } catch (e) {
      if (e?.response?.status === 402) {
        const w = e.response.data?.detail;
        if (w && typeof w === "object") qc.setQueryData(["tryon-credits"], w);
        setShowPaywall(true);
      }
      toast.error(apiError(e));
    } finally {
      setGenerating(false);
    }
  };

  const phoneOk = (raw) => {
    let digits = String(raw || "").replace(/\D+/g, "");
    if (digits.startsWith("91") && digits.length === 12) digits = digits.slice(2);
    return digits.length >= 10 && digits.length <= 15;
  };

  const buyPack = async (packId, provider) => {
    const id = packId || buyPackId;
    if (!id) return toast.error("Pick a credit pack");
    if (provider === "cashfree" && !phoneOk(payPhone)) {
      setPhoneError("Enter a 10-digit mobile number for UPI / Cashfree.");
      return;
    }
    setPhoneError("");
    setBuying(`${provider}:${id}`);
    try {
      const { data } = await api.post("/ai/tryon/credits/checkout", {
        pack_id: id,
        provider,
        phone: payPhone,
        origin_url: window.location.origin,
      });
      if (data?.payment_session_id) await launchCashfree(data);
      else if (data?.checkout_url) window.location.href = data.checkout_url;
    } catch (e) {
      const msg = apiError(e);
      if (/10-digit|mobile number/i.test(msg)) setPhoneError(msg);
      else toast.error(msg);
      setBuying("");
    }
  };

  const openPast = (row) => {
    setResult(row.image_url || "");
    if (row.person_path) setPersonPath(row.person_path);
    const personSrc = row.person_url || (row.person_path ? `/api/files/${row.person_path}` : "");
    if (personSrc) setPersonPreview(imgUrl(personSrc));
    const pid = row.product_id || "";
    if (pid) setSelected(pid);
    const gImg = row.product_image || "";
    const snapshot = {
      id: pid,
      title: row.title || row.product_title || "Previous garment",
      images: gImg ? [gImg] : [],
      brand: row.brand || "",
      price: row.price,
      currency: row.currency || "USD",
    };
    setPastGarment(snapshot);
    if (!gImg && pid) {
      api.get(`/products/${encodeURIComponent(pid)}`).then(({ data }) => {
        setPastGarment((prev) => (prev && prev.id === pid ? {
          ...prev,
          title: data.title || prev.title,
          images: data.images?.length ? data.images : prev.images,
          brand: data.brand || data.store || prev.brand,
          price: data.price ?? prev.price,
          currency: data.currency || prev.currency,
        } : prev));
      }).catch(() => {});
    }
  };

  const restoredPast = useRef(false);
  useEffect(() => {
    if (restoredPast.current || !history.length || personPreview || result) return;
    restoredPast.current = true;
    openPast(history[0]);
  }, [history, personPreview, result]);

  const generateBtn = (testid) => (
    <button
      onClick={generate}
      disabled={generating || !personPath || !selected}
      data-testid={testid}
      className="w-full py-3 btn-accent text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-50"
    >
      {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
      {generating ? "Generating…" : "Generate try-on"}
    </button>
  );

  return (
    <div className="max-w-[1200px] mx-auto px-5 lg:px-8 pt-24 pb-16">
      <div className="sticky top-[68px] z-20 -mx-5 px-5 lg:-mx-8 lg:px-8 py-2.5 mb-4 bg-aura-bg/95 backdrop-blur border-b border-aura-border">
        <div className="flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] tracking-luxe uppercase text-aura-accent">Virtual try-on</div>
            <h1 className="font-serif text-2xl sm:text-3xl tracking-tight leading-none">See it on you</h1>
          </div>
          <button
            type="button"
            onClick={() => setShowPaywall(true)}
            className="text-right shrink-0"
            data-testid="tryon-wallet"
          >
            <div className="text-[10px] tracking-luxe uppercase text-aura-muted">Tries left</div>
            <div className="font-serif text-xl leading-none">{remaining}</div>
            <div className="text-[10px] text-aura-muted mt-0.5">{cashfreeOn ? "Buy with UPI" : "Buy credits"}</div>
          </button>
          <div className="w-[200px] sm:w-[240px] shrink-0">
            {generateBtn("generate-tryon-btn")}
          </div>
        </div>
        {user?.role === "admin" && (
          <p className="text-[11px] text-aura-muted mt-2">
            Keys live in <Link to="/admin" className="link-underline">Admin → Settings</Link>.
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
        <section>
          <div className="text-[10px] tracking-luxe uppercase text-aura-muted mb-1.5">1 · Your photo</div>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            data-testid="upload-person-btn"
            className="relative w-full h-[360px] sm:h-[420px] border border-aura-border hover:border-aura-accent bg-aura-sand overflow-hidden"
          >
            {personPreview ? (
              <img src={personPreview} alt="You" className="absolute inset-0 w-full h-full object-contain" />
            ) : (
              <span className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-aura-muted text-sm px-4">
                <Upload className="w-5 h-5" /> Upload full-body photo
              </span>
            )}
          </button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="person-file-input" />
          {uploading && <div className="mt-1 text-xs text-aura-muted flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Uploading…</div>}
        </section>

        <section>
          <div className="text-[10px] tracking-luxe uppercase text-aura-muted mb-1.5">2 · Selected</div>
          <div className="relative w-full h-[360px] sm:h-[420px] border border-aura-border bg-aura-sand overflow-hidden" data-testid="tryon-selected">
            {selectedProduct?.images?.[0] ? (
              <img src={imgUrl(selectedProduct.images[0])} alt="" className="absolute inset-0 w-full h-full object-contain" />
            ) : (
              <span className="absolute inset-0 flex items-center justify-center text-xs text-aura-muted px-4 text-center">Pick a piece from the grid below.</span>
            )}
            {selectedProduct && (
              <div className="absolute bottom-0 left-0 right-0 px-3 py-2.5 bg-gradient-to-t from-black/75 to-transparent">
                <div className="text-sm line-clamp-2">{selectedProduct.title}</div>
                <div className="text-[11px] text-aura-muted">
                  {selectedProduct.brand || selectedProduct.store || ""} · {formatMoney(selectedProduct.price, selectedProduct.currency || "USD")}
                </div>
              </div>
            )}
          </div>
          {result && !generating && (
            <button type="button" onClick={generate} className="mt-1.5 w-full py-2 border border-aura-border text-xs inline-flex items-center justify-center gap-1.5" data-testid="regenerate-btn">
              <RefreshCw className="w-3.5 h-3.5" /> Again
            </button>
          )}
        </section>

        <section>
          <div className="text-[10px] tracking-luxe uppercase text-aura-muted mb-1.5">3 · On you</div>
          <div className="relative w-full h-[360px] sm:h-[420px] bg-aura-sand border border-aura-border overflow-hidden" data-testid="tryon-result">
            {generating ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-aura-muted">
                <Loader2 className="w-6 h-6 animate-spin" />
                <span className="text-xs">Up to a minute…</span>
              </div>
            ) : result ? (
              <img src={imgUrl(result)} alt="Try-on result" className="absolute inset-0 w-full h-full object-contain fade-up" />
            ) : (
              <span className="absolute inset-0 flex items-center justify-center text-xs text-aura-muted px-4 text-center">Preview appears here</span>
            )}
          </div>
        </section>
      </div>

      {history.length > 0 && (
        <div className="mb-5" data-testid="tryon-history">
          <div className="text-[10px] tracking-luxe uppercase text-aura-muted mb-2">Previous tries · {history.length}</div>
          <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-2">
            {history.map((row, i) => (
              <button
                key={row.id || row.image_path || `try-${i}`}
                type="button"
                onClick={() => openPast(row)}
                className={`text-left border ${result && row.image_url === result ? "border-aura-accent" : "border-aura-border hover:border-aura-ink/40"}`}
                data-testid="tryon-history-item"
              >
                <div className="relative h-24 bg-aura-sand overflow-hidden">
                  {row.image_url ? <img src={imgUrl(row.image_url)} alt="" className="absolute inset-0 w-full h-full object-contain" /> : null}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <section>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <div className="text-[10px] tracking-luxe uppercase text-aura-muted">Pick a garment</div>
          <form
            className="flex gap-2"
            onSubmit={(e) => { e.preventDefault(); setGarmentQ(garmentDraft.trim()); }}
          >
            <input
              value={garmentDraft}
              onChange={(e) => setGarmentDraft(e.target.value)}
              placeholder="Search clothing…"
              data-testid="garment-search"
              className="w-44 sm:w-56 px-2.5 py-1.5 border border-aura-border bg-transparent outline-none text-sm"
            />
            <button type="submit" className="px-2.5 py-1.5 text-xs border border-aura-border hover:border-aura-accent">Find</button>
          </form>
        </div>

        <div className="flex flex-wrap gap-1.5 mb-2" data-testid="tryon-categories">
          {["All", ...APPAREL_CATEGORIES].map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => { setGarmentCat(c); setBrandId(""); }}
              className={`text-[11px] px-2.5 py-1 border ${garmentCat === c ? "border-aura-accent text-aura-accent" : "border-aura-border text-aura-muted hover:text-aura-ink"}`}
            >
              {c === "All" ? "All clothing" : c}
            </button>
          ))}
        </div>

        {brands.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3" data-testid="tryon-brands">
            <button
              type="button"
              onClick={() => setBrandId("")}
              className={`text-[11px] px-2.5 py-1 border ${!brandId ? "border-aura-accent text-aura-accent" : "border-aura-border text-aura-muted"}`}
            >
              All brands
            </button>
            {brands.slice(0, 14).map((b) => (
              <button
                key={b.id || b.name}
                type="button"
                onClick={() => setBrandId(b.id)}
                className={`text-[11px] px-2.5 py-1 border ${brandId === b.id ? "border-aura-accent text-aura-accent" : "border-aura-border text-aura-muted"}`}
              >
                {b.name}
              </button>
            ))}
          </div>
        )}

        {loadingGarments ? (
          <div className="py-8 text-sm text-aura-muted flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading garments…</div>
        ) : (
          <>
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2 max-h-[340px] overflow-y-auto pr-1" data-testid="garment-grid">
              {products.map((p) => {
                const on = p.id === selected;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => { setSelected(p.id); setPastGarment(null); setResult(null); }}
                    data-testid="garment-card"
                    className={`text-left border ${on ? "border-aura-accent" : "border-aura-border hover:border-aura-ink/40"}`}
                  >
                    <div className="relative h-24 bg-aura-sand overflow-hidden">
                      {p.images?.[0] ? <img src={imgUrl(p.images[0])} alt="" className="absolute inset-0 w-full h-full object-contain" /> : null}
                    </div>
                    <div className="px-1.5 py-1.5">
                      <div className="text-[11px] line-clamp-2 leading-snug">{p.title}</div>
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="flex items-center justify-between gap-3 mt-3">
              <p className="text-[11px] text-aura-muted">{products.length} pieces</p>
              {pageToken && (
                <button
                  type="button"
                  onClick={loadMore}
                  disabled={loadingMore}
                  data-testid="tryon-load-more"
                  className="text-xs tracking-luxe uppercase border border-aura-border px-4 py-2 hover:border-aura-accent disabled:opacity-50 inline-flex items-center gap-2"
                >
                  {loadingMore ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                  {loadingMore ? "Loading…" : "Load more"}
                </button>
              )}
            </div>
          </>
        )}
      </section>

      {showPaywall && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end justify-center" data-testid="tryon-paywall">
          <div className="bg-aura-surface border-t border-aura-border max-w-lg w-full p-6 pb-8">
            <div className="text-[10px] tracking-luxe uppercase text-aura-muted mb-2">AI credits</div>
            <h3 className="font-serif text-2xl mb-2">You have used your free try-ons</h3>
            <p className="text-sm text-aura-muted mb-5">
              The first 3 virtual try-ons are free. Buy a pack to keep generating looks — tokens are spent only when a try-on succeeds.
              {" "}Pick Cashfree if you pay from India, or Stripe for a card from another country.
            </p>
            <div className="grid gap-3 mb-5">
              {(wallet?.packs || []).map((pack) => {
                const on = buyPackId === pack.id;
                return (
                  <button
                    key={pack.id}
                    type="button"
                    disabled={Boolean(buying)}
                    onClick={() => setBuyPackId(pack.id)}
                    data-testid="tryon-pack"
                    className={`flex items-center justify-between px-4 py-3 border text-left disabled:opacity-60 ${on ? "border-aura-accent" : "border-aura-border hover:border-aura-accent"}`}
                  >
                    <span>{pack.label}</span>
                    <span className="font-mono text-sm">
                      {pack.inr != null ? `₹${Number(pack.inr).toLocaleString("en-IN")}` : `$${Number(pack.usd).toFixed(2)}`}
                      {pack.inr != null ? <span className="text-aura-muted"> · ${Number(pack.usd).toFixed(2)}</span> : null}
                    </span>
                  </button>
                );
              })}
            </div>
            {!cashfreeOn && !stripeOn && (
              <p className="text-sm text-aura-muted mb-4">Checkout is not configured yet. Ask admin to add Cashfree or Stripe under Settings.</p>
            )}
            <div className="border-t border-aura-border pt-4">
              <PayMethodPicker methods={payMethods} value={payProvider} onChange={setPayProvider} note={payNote} />
              {payProvider === "cashfree" && (
                <label className="block mt-3 mb-3">
                  <span className="block text-sm mb-2">Mobile number for UPI / Cashfree</span>
                  <input
                    value={payPhone}
                    onChange={(e) => { setPayPhone(e.target.value); if (phoneError) setPhoneError(""); }}
                    placeholder="10-digit mobile"
                    inputMode="tel"
                    data-testid="tryon-cashfree-phone"
                    className={`w-full px-4 py-3 bg-aura-bg border outline-none text-sm ${phoneError ? "border-aura-accent" : "border-aura-border focus:border-aura-accent"}`}
                  />
                  {phoneError ? (
                    <p className="text-sm text-aura-accent mt-2" data-testid="tryon-phone-error">{phoneError}</p>
                  ) : (
                    <p className="text-xs text-aura-muted mt-2">Required for UPI and Indian cards.</p>
                  )}
                </label>
              )}
              {payProvider && (
                <button
                  type="button"
                  disabled={Boolean(buying)}
                  onClick={() => buyPack(buyPackId, payProvider)}
                  data-testid={payProvider === "cashfree" ? "tryon-pay-cashfree" : "tryon-pay-stripe"}
                  className="mt-3 w-full py-3.5 btn-accent text-sm font-semibold disabled:opacity-60"
                >
                  {buying ? "Opening checkout…" : payProvider === "cashfree" ? "Pay with Cashfree" : "Pay with Stripe"}
                </button>
              )}
              <button type="button" onClick={() => { setShowPaywall(false); setPhoneError(""); }} className="text-xs link-underline mt-4">Not now</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
